"use client";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { LoaderCircleIcon } from "lucide-react";

const buttonVariants = cva(
  "relative hover:cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap w-fit bg-bg-surface active:bg-bg-surface-active hover:bg-bg-surface-hover rounded-(--border-radius-200) shadow-sm border-(length:--border-width-025) border-input-border text-primary font-medium hover:transition-colors hover:duration-200 disabled:pointer-events-none disabled:bg-bg-surface-disabled disabled:text-disabled disabled:border-input-border [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand",
  {
    variants: {
      variant: {
        ghost: "bg-transparent border-none text-primary hover:bg-transparent active:bg-transparent",
        menu: "border-none body-md bg-bg-fill-inverse hover:bg-bg-fill-inverse-hover active:bg-bg-fill-inverse-active flex items-center gap-3 text-bg",
        default: "",
        primary:
          "bg-bg-fill-brand hover:bg-bg-fill-brand-hover active:bg-bg-fill-brand-active text-inverse border-bg-fill-brand",
        tertiary: "border-none shadow-none",
        critical:
          "bg-bg-fill-critical hover:bg-bg-fill-critical-hover active:bg-bg-fill-critical-active text-inverse border-border-critical",
        link: "text-primary underline-offset-4 hover:underline",
        action:
          "bg-surface-secondary border-none shadow-none hover:bg-bg-surface-secondary-hover active:bg-bg-surface-secondary-active px-2 py-1",
      },
      size: {
        default: "px-4 py-1 h-9 has-[>svg]:px-4 has-[>svg]:px-2.5 body-lg",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-4 has-[>svg]:px-2.5 body-sm ",
        lg: "h-8 p-0.5 has-[>svg]:px-4 body-lg",
        icon: "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  isLoading = false,
  asChild = false,
  children,
  innerClassName,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    innerClassName?: string;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={isLoading || props.disabled}
      {...props}
    >
      <span
        className={cn(
          "inline-flex items-center justify-center gap-2 text-inherit",
          innerClassName,
          isLoading ? "invisible" : ""
        )}
      >
        {children}
      </span>

      {isLoading && (
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
          <LoaderCircleIcon className="animate-spin size-4 text-current" />
        </span>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
